import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Paper,
  TextField,
  MenuItem,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Slider,
  Divider,
  IconButton,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Restaurant as RestaurantIcon,
  MeetingRoom as EventSpaceIcon,
  Hotel as ResortIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { getAllPublicPackages } from '../../services/venueService';
import { PackageType } from '../../types/Venue';

const ExplorePackages: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const [packages, setPackages] = useState<any[]>([]);
  const [filteredPackages, setFilteredPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [priceRange, setPriceRange] = useState<number[]>([0, 500]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortOption, setSortOption] = useState('newest');
  
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const data = await getAllPublicPackages(50);
        setPackages(data);
        setFilteredPackages(data);
        
        // Find min and max price for slider
        if (data.length > 0) {
          const prices = data.map(pkg => pkg.price);
          const minPrice = Math.floor(Math.min(...prices));
          const maxPrice = Math.ceil(Math.max(...prices));
          setPriceRange([minPrice, maxPrice]);
        }
      } catch (err) {
        console.error('Error fetching packages:', err);
        setError('Failed to load packages. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPackages();
  }, []);
  
  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedType, priceRange, sortOption, packages]);
  
  const applyFilters = () => {
    let results = [...packages];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      results = results.filter(pkg => 
        pkg.name.toLowerCase().includes(query) || 
        pkg.description.toLowerCase().includes(query) ||
        pkg.venueName.toLowerCase().includes(query) ||
        (pkg.venueLocation?.city && pkg.venueLocation.city.toLowerCase().includes(query))
      );
    }
    
    // Apply package type filter
    if (selectedType) {
      results = results.filter(pkg => pkg.packageType === selectedType);
    }
    
    // Apply price range filter
    results = results.filter(pkg => 
      pkg.price >= priceRange[0] && pkg.price <= priceRange[1]
    );
    
    // Apply sorting
    switch (sortOption) {
      case 'newest':
        results.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB.getTime() - dateA.getTime();
        });
        break;
      case 'priceAsc':
        results.sort((a, b) => a.price - b.price);
        break;
      case 'priceDesc':
        results.sort((a, b) => b.price - a.price);
        break;
      default:
        break;
    }
    
    setFilteredPackages(results);
  };
  
  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedType('');
    setPriceRange([0, Math.max(...packages.map(pkg => pkg.price))]);
    setSortOption('newest');
  };
  
  const handlePackageClick = (venueId: string, packageId: string) => {
    navigate(`/explore/packages/${venueId}/${packageId}`);
  };
  
  const formatPriceRange = (value: number) => {
    return `$${value}`;
  };

  const renderVenueTypeIcon = (type: string) => {
    switch (type) {
      case 'restaurant':
        return <RestaurantIcon fontSize="small" />;
      case 'event_space':
        return <EventSpaceIcon fontSize="small" />;
      case 'resort':
        return <ResortIcon fontSize="small" />;
      default:
        return <LocationIcon fontSize="small" />;
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Discover Date Packages
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" align="center" gutterBottom>
          Find the perfect experience for your special occasion
        </Typography>
      </Box>
      
      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Search packages by name, venue, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{ mr: 1 }}
          />
          <IconButton 
            color={showFilters ? "primary" : "default"}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ flexShrink: 0 }}
          >
            <FilterIcon />
          </IconButton>
        </Box>
        
        {/* Filters */}
        {showFilters && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Package Type</InputLabel>
                  <Select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    label="Package Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value={PackageType.Dinner}>Dinner</MenuItem>
                    <MenuItem value={PackageType.Experience}>Experience</MenuItem>
                    <MenuItem value={PackageType.Event}>Event</MenuItem>
                    <MenuItem value={PackageType.Special}>Special Occasion</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    label="Sort By"
                  >
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="priceAsc">Price: Low to High</MenuItem>
                    <MenuItem value="priceDesc">Price: High to Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Box sx={{ px: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Price Range: {formatPriceRange(priceRange[0])} - {formatPriceRange(priceRange[1])}
                  </Typography>
                  <Slider
                    value={priceRange}
                    onChange={(e, newValue) => setPriceRange(newValue as number[])}
                    valueLabelDisplay="auto"
                    valueLabelFormat={formatPriceRange}
                    min={0}
                    max={Math.max(500, ...packages.map(pkg => Math.ceil(pkg.price)))}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetFilters}
                startIcon={<ClearIcon />}
              >
                Reset Filters
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
      
      {/* Results */}
      {error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      ) : filteredPackages.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No packages found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or search criteria
          </Typography>
          <Button
            variant="outlined"
            onClick={handleResetFilters}
            sx={{ mt: 2 }}
          >
            Reset Filters
          </Button>
        </Paper>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              {filteredPackages.length} {filteredPackages.length === 1 ? 'package' : 'packages'} found
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            {filteredPackages.map((pkg) => (
              <Grid item xs={12} sm={6} md={4} key={`${pkg.venueId}-${pkg.id}`}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                      cursor: 'pointer'
                    }
                  }}
                  onClick={() => handlePackageClick(pkg.venueId, pkg.id)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={pkg.images[0] || 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'}
                      alt={pkg.name}
                    />
                    
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        bottom: 10, 
                        right: 10,
                        bgcolor: 'primary.main',
                        color: 'white',
                        py: 0.5,
                        px: 1.5,
                        borderRadius: 1,
                        fontWeight: 'bold'
                      }}
                    >
                      ${pkg.price.toFixed(2)}
                    </Box>
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Chip
                        size="small"
                        label={pkg.packageType.charAt(0).toUpperCase() + pkg.packageType.slice(1)}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    
                    <Typography variant="h6" component="h2" noWrap gutterBottom>
                      {pkg.name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {renderVenueTypeIcon(pkg.venueType)}
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                        {pkg.venueName}
                      </Typography>
                    </Box>
                    
                    {pkg.venueLocation && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationIcon fontSize="small" />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }} noWrap>
                          {pkg.venueLocation.city}, {pkg.venueLocation.state}
                        </Typography>
                      </Box>
                    )}
                    
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {pkg.description}
                    </Typography>
                  </CardContent>
                  
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button 
                      size="small" 
                      variant="contained"
                      fullWidth
                    >
                      View Details
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
};

export default ExplorePackages;
